import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { tenderService } from '../services/tenderService';
import { tenderTermsService } from '../services/tenderTermsService';

/**
 * Tender store using standardized API client
 */
export const useTenderStore = defineStore('tenders', () => {
  // State
  const tenders = ref([]);
  const currentTender = ref(null);
  const isLoading = ref(false);
  const error = ref(null);
  const filterStatus = ref('all');
  const searchQuery = ref('');
  const contractors = ref([]);
  const quotes = ref([]);
  const termsLibrary = ref({
    inclusions: [],
    exclusions: [],
    qualifications: [],
  });
  const currentTerms = ref({
    inclusions: [],
    exclusions: [],
    qualifications: [],
  });
  const pagination = ref({
    currentPage: 1,
    perPage: 20,
    totalItems: 0,
    totalPages: 0,
  });
  
  // Status color mappings
  const statusColor = {
    pending: 'orange',
    submitted: 'blue',
    won: 'positive',
    lost: 'negative',
  };
  
  // Getters
  const filteredTenders = computed(() => {
    return tenders.value.filter((tender) => {
      // Apply status filter
      if (filterStatus.value !== 'all' && tender.status !== filterStatus.value) {
        return false;
      }
      
      // Apply search filter
      if (searchQuery.value && !tender.name.toLowerCase().includes(searchQuery.value.toLowerCase()) && 
          !tender.reference_number?.toLowerCase().includes(searchQuery.value.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  });
  
  const statusOptions = computed(() => [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Won', value: 'won' },
    { label: 'Lost', value: 'lost' },
    { label: 'DNQ', value: 'dnq' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Withdrew', value: 'withdrew' },
    { label: 'Dead', value: 'dead' },
  ]);
  
  const getTenderById = computed(() => {
    return (id) => tenders.value.find((tender) => tender.id === parseInt(id));
  });
  
  /**
   * Fetch all tenders with optional filtering and pagination
   * @param {Object} options - Fetch options
   * @returns {Promise<Array>} Fetched tenders
   */
  const fetchTenders = async (options = {}) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      
      // Build params for API call
      const params = { ...options };
      
      // Map status filter to API parameter
      if (filterStatus.value !== 'all') {
        params.status = filterStatus.value;
      }
      
      // Map search query to API parameter
      if (searchQuery.value) {
        params.q = searchQuery.value;
      }
      
      // Use standardized service to fetch tenders
      const result = await tenderService.getTenders(params);
      
      // Extract tenders and pagination info
      const fetchedTenders = result.data || [];
      
      // Update pagination if available
      if (result.pagination) {
        pagination.value = {
          currentPage: result.pagination.current_page,
          perPage: result.pagination.per_page,
          totalItems: result.pagination.total_items,
          totalPages: result.pagination.total_pages,
        };
      }
      
      
      // Update tenders based on options
      if (options.merge) {
        // Merge with existing tenders (avoid duplicates)
        const tenderMap = new Map();
        
        // First add existing tenders to map
        tenders.value.forEach((tender) => {
          if (tender.id) {
            tenderMap.set(tender.id, tender);
          }
        });
        
        // Then add/update with fetched tenders
        fetchedTenders.forEach((tender) => {
          if (tender.id) {
            tenderMap.set(tender.id, tender);
          }
        });
        
        // Convert map values back to array
        tenders.value = Array.from(tenderMap.values());
      } else {
        // Replace tenders array
        tenders.value = fetchedTenders;
      }
      
      return fetchedTenders;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Fetch a single tender by ID
   * @param {number|string} id - Tender ID
   * @param {Array|string} include - Related data to include
   * @returns {Promise<Object>} Fetched tender
   */
  const fetchTender = async (id, include = []) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const tender = await tenderService.getTender(id, include);
      
      // Update current tender
      currentTender.value = tender;
      
      // Also update this tender in the tenders array if it exists
      const index = tenders.value.findIndex((t) => t.id === parseInt(id));
      if (index !== -1) {
        tenders.value[index] = tender;
      } else {
        // Add to array if not present
        tenders.value.push(tender);
      }
      
      return tender;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Create a new tender
   * @param {Object} tenderData - Tender data
   * @returns {Promise<Object>} Created tender
   */
  const createTender = async (tenderData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const newTender = await tenderService.createTender(tenderData);
      
      // Add the new tender to the beginning of the list
      tenders.value.unshift(newTender);
      
      return newTender;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Update an existing tender
   * @param {number|string} id - Tender ID
   * @param {Object} tenderData - Updated tender data
   * @returns {Promise<Object>} Updated tender
   */
  const updateTender = async (id, tenderData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const updatedTender = await tenderService.updateTender(id, tenderData);
      
      // Update the tenders array with the updated tender
      const index = tenders.value.findIndex((t) => t.id === parseInt(id));
      if (index !== -1) {
        tenders.value[index] = updatedTender;
      }
      
      // Update current tender if it's the same as the one being updated
      if (currentTender.value && currentTender.value.id === parseInt(id)) {
        currentTender.value = updatedTender;
      }
      
      return updatedTender;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Delete a tender
   * @param {number|string} id - Tender ID
   * @returns {Promise<Object>} Deletion result
   */
  const deleteTender = async (id) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const result = await tenderService.deleteTender(id);
      
      // Remove the tender from the tenders array
      tenders.value = tenders.value.filter((t) => t.id !== parseInt(id));
      
      // Reset current tender if it's the one being deleted
      if (currentTender.value && currentTender.value.id === parseInt(id)) {
        currentTender.value = null;
      }
      
      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Add a contractor to a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} contractorData - Contractor data
   * @returns {Promise<Object>} Added contractor
   */
  const addContractor = async (tenderId, contractorData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const contractor = await tenderService.addContractor(tenderId, contractorData);
      
      // Update the current tender if it's the one being modified
      if (currentTender.value && currentTender.value.id === parseInt(tenderId)) {
        // Add to contractors array if it doesn't already include this one
        if (!currentTender.value.contractors) {
          currentTender.value.contractors = [];
        }
        currentTender.value.contractors.push(contractor);
      }
      
      return contractor;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Update a contractor in a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} contractorId - Contractor ID
   * @param {Object} contractorData - Updated contractor data
   * @returns {Promise<Object>} Updated contractor
   */
  const updateContractor = async (tenderId, contractorId, contractorData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const contractor = await tenderService.updateContractor(tenderId, contractorId, contractorData);
      
      // Update the current tender if it's the one being modified
      if (currentTender.value && currentTender.value.id === parseInt(tenderId) && 
          currentTender.value.contractors) {
        // Find and update the contractor
        const index = currentTender.value.contractors.findIndex((c) => c.id === parseInt(contractorId));
        if (index !== -1) {
          currentTender.value.contractors[index] = contractor;
        }
      }
      
      return contractor;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Remove a contractor from a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} contractorId - Contractor ID
   * @returns {Promise<Object>} Removal result
   */
  const removeContractor = async (tenderId, contractorId) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const result = await tenderService.removeContractor(tenderId, contractorId);
      
      // Update the current tender if it's the one being modified
      if (currentTender.value && currentTender.value.id === parseInt(tenderId) && 
          currentTender.value.contractors) {
        // Remove the contractor from the array
        currentTender.value.contractors = currentTender.value.contractors.filter(
          (c) => c.id !== parseInt(contractorId),
        );
      }
      
      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Add a quote to a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Added quote
   */
  const addQuote = async (tenderId, quoteData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const quote = await tenderService.addQuote(tenderId, quoteData);
      
      // Update the current tender if it's the one being modified
      if (currentTender.value && currentTender.value.id === parseInt(tenderId)) {
        // Add to quotes array if it doesn't already include this one
        if (!currentTender.value.quotes) {
          currentTender.value.quotes = [];
        }
        currentTender.value.quotes.push(quote);
      }
      
      return quote;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Update a quote in a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {Object} quoteData - Updated quote data
   * @returns {Promise<Object>} Updated quote
   */
  const updateQuote = async (tenderId, quoteId, quoteData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const quote = await tenderService.updateQuote(tenderId, quoteId, quoteData);
      
      // Update the current tender if it's the one being modified
      if (currentTender.value && currentTender.value.id === parseInt(tenderId) && 
          currentTender.value.quotes) {
        // Find and update the quote
        const index = currentTender.value.quotes.findIndex((q) => q.id === parseInt(quoteId));
        if (index !== -1) {
          currentTender.value.quotes[index] = quote;
        }
      }
      
      return quote;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Remove a quote from a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<Object>} Removal result
   */
  const removeQuote = async (tenderId, quoteId) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const result = await tenderService.removeQuote(tenderId, quoteId);
      
      // Update the current tender if it's the one being modified
      if (currentTender.value && currentTender.value.id === Number(tenderId) &&
          currentTender.value.quotes) {
        // Remove the quote from the array
        currentTender.value.quotes = currentTender.value.quotes.filter(
          (q) => q.id !== Number(quoteId),
        );
      }
      
      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Add a comment to a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} commentData - Comment data
   * @returns {Promise<Object>} Added comment
   */
  const addComment = async (tenderId, commentData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const comment = await tenderService.addComment(tenderId, commentData);
      
      // Update the current tender if it's the one being modified
      if (currentTender.value && currentTender.value.id === parseInt(tenderId)) {
        // Add to comments array if it doesn't already include this one
        if (!currentTender.value.comments) {
          currentTender.value.comments = [];
        }
        currentTender.value.comments.push(comment);
      }
      
      return comment;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Fetch activities for a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise<Array>} Tender activities
   */
  const fetchActivities = async (tenderId, params = {}) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const result = await tenderService.getActivities(tenderId, params);
      
      // Update the current tender if it's the one being fetched
      if (currentTender.value && currentTender.value.id === parseInt(tenderId)) {
        currentTender.value.activities = result.data;
      }
      
      return result.data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Send a tender to contractors
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {Object} sendData - Send options (recipients, message)
   * @returns {Promise<Object>} Send result
   */
  const sendTender = async (tenderId, quoteId, sendData) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const result = await tenderService.sendTender(tenderId, quoteId, sendData);
      
      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Set the filter status
   * @param {string} status - Status filter value
   */
  const setFilterStatus = (status) => {
    filterStatus.value = status;
  };
  
  /**
   * Set the search query
   * @param {string} query - Search query
   */
  const setSearchQuery = (query) => {
    searchQuery.value = query;
  };
  
  /**
   * Reset filters
   */
  const resetFilters = () => {
    filterStatus.value = 'all';
    searchQuery.value = '';
  };
  
  /**
   * Fetch terms library for global terms
   * @returns {Promise<Object>} Terms library items
   */
  const fetchTermsLibrary = async () => {
    try {
      error.value = null;
      isLoading.value = true;
      return await tenderTermsService.getTermsLibrary();
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Add an item to the terms library
   * @param {string} type - Type of term (inclusion, exclusion, qualification)
   * @param {string} text - Term text
   * @returns {Promise<Object>} Created library item
   */
  const addToTermsLibrary = async (type, text) => {
    try {
      error.value = null;
      isLoading.value = true;
      return await tenderTermsService.addToLibrary(type, text);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch terms for a tender
   * @param {number|string} tenderId - Tender ID
   * @returns {Promise<Object>} Tender terms
   */
  const fetchTenderTerms = async (tenderId) => {
    try {
      error.value = null;
      isLoading.value = true;
      return await tenderTermsService.getTenderTerms(tenderId);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update tender terms
   * @param {number|string} tenderId - Tender ID
   * @param {Object} termsData - Terms data to update
   * @returns {Promise<Object>} Updated tender terms
   */
  const updateTenderTerms = async (tenderId, termsData) => {
    try {
      error.value = null;
      isLoading.value = true;
      return await tenderTermsService.updateTenderTerms(tenderId, termsData);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Phase 3: Get quote options for a tender
   * @param {number|string} tenderId - Tender ID
   * @returns {Promise<Array>} Quote options with latest versions
   */
  const getQuoteOptions = async (tenderId) => {
    try {
      error.value = null;
      const result = await tenderService.getQuoteOptions(tenderId);
      return Array.isArray(result) ? result : [];
    } catch (err) {
      error.value = err.message;
      throw err;
    }
  };

  /**
   * Phase 3: Create a quote for a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Created quote
   */
  const createQuoteForTender = async (tenderId, quoteData) => {
    try {
      error.value = null;
      isLoading.value = true;

      const result = await tenderService.addQuote(tenderId, quoteData);


      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    tenders,
    currentTender,
    isLoading,
    error,
    filterStatus,
    searchQuery,
    contractors,
    quotes,
    termsLibrary,
    currentTerms,
    pagination,
    statusColor,
    
    // Getters
    filteredTenders,
    statusOptions,
    getTenderById,
    
    // Actions
    fetchTenders,
    fetchTender,
    createTender,
    updateTender,
    deleteTender,
    addContractor,
    updateContractor,
    removeContractor,
    addQuote,
    updateQuote,
    removeQuote,
    addComment,
    fetchActivities,
    sendTender,
    setFilterStatus,
    setSearchQuery,
    resetFilters,
    
    // Terms functionality
    fetchTermsLibrary,
    addToTermsLibrary,
    fetchTenderTerms,
    updateTenderTerms,

    // Phase 3: Quote options functionality
    getQuoteOptions,
    createQuoteForTender,
  };
});